import type { GraphQLClient } from 'graphql-request'

// Zunou Memory — the scoped facts layer (ORG / PULSE / AGENT → category →
// key → value). Glass Office surfaces AGENT scope in the Manage panel,
// mirroring the dashboard's Workforce MemoryPanel. Container privacy is
// enforced server-side (AssertsMemoryScopeAccess).

export type MemoryScope = 'ORG' | 'PULSE' | 'AGENT'

export interface MemoryRecordLite {
  id: string
  category: string
  key: string
  valueType: string
  value: string
  tags: string[]
  pinned: boolean
  updatedAt: string
  updaterName: string | null
}

export interface MemoryCategoryLite {
  category: string
  count: number
  updatedAt: string | null
}

const CATEGORIES_QUERY = /* GraphQL */ `
  query GlassOfficeMemoryCategories($organizationId: ID!, $scopeType: MemoryScopeType!, $scopeId: String) {
    memoryCategories(organizationId: $organizationId, scopeType: $scopeType, scopeId: $scopeId) {
      category
      count
      updatedAt
    }
  }
`

export async function fetchMemoryCategories(
  client: GraphQLClient,
  params: { organizationId: string; scopeType: MemoryScope; scopeId?: string },
): Promise<MemoryCategoryLite[]> {
  const data = await client.request<{
    memoryCategories?: Array<{
      category: string
      count: number
      updatedAt?: string | null
    }> | null
  }>(CATEGORIES_QUERY, {
    organizationId: params.organizationId,
    scopeType: params.scopeType,
    scopeId: params.scopeId ?? null,
  })
  return (data.memoryCategories ?? []).map((c) => ({
    category: c.category,
    count: c.count,
    updatedAt: c.updatedAt ?? null,
  }))
}

const RECORDS_QUERY = /* GraphQL */ `
  query GlassOfficeMemoryRecords(
    $organizationId: ID!
    $scopeType: MemoryScopeType!
    $scopeId: String
    $category: String
    $search: String
  ) {
    memoryRecords(
      organizationId: $organizationId
      scopeType: $scopeType
      scopeId: $scopeId
      category: $category
      search: $search
      pinnedFirst: true
      first: 100
    ) {
      items {
        id
        category
        key
        valueType
        value
        tags
        pinned
        updatedAt
        updater {
          name
        }
      }
      total
    }
  }
`

export async function fetchMemoryRecords(
  client: GraphQLClient,
  params: {
    organizationId: string
    scopeType: MemoryScope
    scopeId?: string
    category?: string
    search?: string
  },
): Promise<{ records: MemoryRecordLite[]; total: number }> {
  const data = await client.request<{
    memoryRecords?: {
      items?: Array<{
        id: string
        category: string
        key: string
        valueType?: string | null
        value: string
        tags?: string[] | null
        pinned?: boolean | null
        updatedAt?: string | null
        updater?: { name?: string | null } | null
      }> | null
      total?: number | null
    } | null
  }>(RECORDS_QUERY, {
    organizationId: params.organizationId,
    scopeType: params.scopeType,
    scopeId: params.scopeId ?? null,
    category: params.category ?? null,
    search: params.search ?? null,
  })
  return {
    records: (data.memoryRecords?.items ?? []).map((r) => ({
      id: r.id,
      category: r.category,
      key: r.key,
      valueType: (r.valueType ?? 'TEXT').toUpperCase(),
      value: r.value,
      tags: r.tags ?? [],
      pinned: Boolean(r.pinned),
      updatedAt: r.updatedAt ?? '',
      updaterName: r.updater?.name ?? null,
    })),
    total: data.memoryRecords?.total ?? 0,
  }
}

const UPSERT_MEMORY = /* GraphQL */ `
  mutation GlassOfficeUpsertMemory($input: UpsertMemoryRecordInput!) {
    upsertMemoryRecord(input: $input) {
      id
    }
  }
`

export async function upsertMemoryRecord(
  client: GraphQLClient,
  params: {
    organizationId: string
    scopeType: MemoryScope
    scopeId?: string
    category: string
    key: string
    value: string
    pinned?: boolean
  },
): Promise<void> {
  await client.request(UPSERT_MEMORY, {
    input: {
      organizationId: params.organizationId,
      scopeType: params.scopeType,
      ...(params.scopeId ? { scopeId: params.scopeId } : {}),
      category: params.category,
      key: params.key,
      valueType: 'TEXT',
      value: params.value,
      ...(params.pinned !== undefined ? { pinned: params.pinned } : {}),
    },
  })
}

const DELETE_MEMORY = /* GraphQL */ `
  mutation GlassOfficeDeleteMemory($id: ID!) {
    deleteMemoryRecord(id: $id)
  }
`

export async function deleteMemoryRecord(
  client: GraphQLClient,
  id: string,
): Promise<void> {
  await client.request(DELETE_MEMORY, { id })
}
